import { useLayoutEffect, useMemo, useRef } from "react";
import { Layer, Stage } from "react-konva";
import { finalizeImages, RenderPrimitiveAsKonva, renderDatatable } from "./RenderHelpers";
import FlowInstanceOutput from "./FlowInstanceOutput";
import InfiniteCanvas from "./InfiniteCanvas";
import { buildViewRenderInput } from "./utils/viewRendering";

const SUMMARY_WIDTH_MIN_RATIO = 12 / 9;
const SUMMARY_WIDTH_INCREMENT = 900;
const MAX_RENDER_ITERATIONS = 5;

function buildViewPrimitiveNode(primitive, { width, height, imageCallback, lockDimensions = false }) {
  const viewRenderInput = buildViewRenderInput({
    primitive,
    width,
    height,
    lockDimensions,
  });
  if (!viewRenderInput) {
    return null;
  }
  return renderDatatable({
    ...viewRenderInput,
    stageOptions: {
      x: 0,
      y: 0,
      imageCallback,
    },
  });
}

export function KonvaPrimitive({ primitive, flowOutputNames, ...props }) {
  const stageRef = useRef();
  const isZoomEnabled = Boolean(props.enableZoom);
  const baseClassName = props.className ?? (isZoomEnabled ? "flex h-full w-full" : "inline-flex m-2");
  const width = props.width ?? 200;
  const height = props.height ?? (width / 16) * 9;

  const shouldUseZoomCanvas = Boolean(isZoomEnabled && primitive?.type !== "flowinstance");
  const isViewPrimitive = primitive?.type === "view";
  const isPreviewThumbnail = !shouldUseZoomCanvas && (props.preview ?? width <= 480);
  const previewHeightMultiplier = isViewPrimitive && isPreviewThumbnail ? 2 : 1;
  const focusWidth = props.focusWidth ?? 1280;
  const focusHeight = props.focusHeight ?? 720;
  const renderWidth = isViewPrimitive && shouldUseZoomCanvas ? focusWidth : width;
  const renderHeight = isViewPrimitive && shouldUseZoomCanvas ? focusHeight : height;

  const renderConfig = useMemo(() => {
    if (!primitive || primitive.type === "flowinstance") {
      return { items: null, options: null };
    }
    const options = {
      imageCallback: (node) => {
        const l = node.getLayer()
        if(l){
          
          node.refreshCache();
          node.draw();
        }
      },
    };
    if (primitive.type === "view") {
      const viewNode = buildViewPrimitiveNode(
        primitive,
        shouldUseZoomCanvas
          ? {
              width: renderWidth,
              height: renderHeight,
              imageCallback: options.imageCallback,
              lockDimensions: true,
            }
          : {
              width,
              height: height * previewHeightMultiplier,
              imageCallback: options.imageCallback,
              lockDimensions: false,
            }
      );
      if (viewNode) {
        return { items: viewNode, options };
      }
    }
    let items = null;
    let repeat = false;
    let iter = 0;
    do {
      items = RenderPrimitiveAsKonva(primitive, options);
      if (primitive.type === "summary" && items) {
        iter += 1;
        const lWidth = items.width();
        const lHeight = items.height();
        if (lWidth && lHeight && lWidth / lHeight < SUMMARY_WIDTH_MIN_RATIO) {
          options.width = SUMMARY_WIDTH_INCREMENT * (iter + 1);
          repeat = true;
        } else {
          repeat = false;
        }
      } else {
        repeat = false;
      }
    } while (repeat && iter < MAX_RENDER_ITERATIONS);
    return { items, options };
  }, [primitive, renderWidth, renderHeight]);

  const zoomRenderList = useMemo(() => {
    if (!shouldUseZoomCanvas || !primitive) {
      return [];
    }
    const frameId = primitive.id || primitive._id || `konva_${primitive.plainId ?? "preview"}`;
    const title =
      primitive.title ||
      primitive.metadata?.title ||
      primitive.referenceParameters?.title ||
      primitive.type ||
      "Visualization";
    return [
      {
        id: frameId,
        title,
        frameless: true,
        ringless: true,
        canChangeSize: true,
        canvasMargin: [0, 0, 0, 0],
        items: () => renderConfig.items?.clone(),
      },
    ];
  }, [shouldUseZoomCanvas, primitive, renderConfig.items]);

  useLayoutEffect(() => {
    if (shouldUseZoomCanvas || !stageRef.current || !renderConfig.items) {
      return;
    }
    const stage = stageRef.current;
    const layer = stage.children?.[0];
    if (!layer) {
      return;
    }
    const cloned = renderConfig.items.clone();
    const lWidth = cloned.width();
    const lHeight = cloned.height();
    let scale = 1;
    if (lWidth && lHeight) {
      scale = Math.min(width / lWidth, height / lHeight, 1) * 0.9;
    }
    stage.scale({ x: scale, y: scale });
    layer.add(cloned);
    if (lWidth && lHeight) {
      stage.width(lWidth * scale);
      stage.height(lHeight * scale);
    } else {
      stage.width(width);
      stage.height(height);
    }
    const finalizeHandle = setTimeout(() => finalizeImages(cloned), 200);
    return () => clearTimeout(finalizeHandle);
  }, [renderConfig, shouldUseZoomCanvas, width, height]);

  if (primitive?.type === "flowinstance") {
    return (
      <div
        style={{
          width,
          height,
        }}
        className={baseClassName}
      >
        <FlowInstanceOutput
          key={`${primitive?.id ?? primitive?.plainId ?? "flow"}_${flowOutputNames?.[0] ?? "all"}`}
          enableZoom={false}
          enablePan={false}
          enableDrag={props.enableDrag}
          enableTextSelection={props.enableTextSelection}
          background="inherit"
          enableFrameSelection="overlay"
          primitive={primitive}
          initialZoom="height"
          outputNames={flowOutputNames}
          select={(pOrId) => {
            console.log(pOrId);
          }}
        />
      </div>
    );
  }

  if (shouldUseZoomCanvas && renderConfig.items) {
    return (
      <div className="@container flex h-full w-full flex-1 min-h-0 bg-white/90 dark:bg-slate-900/60">
        <div className="flex h-full w-full flex-1 min-h-0 overflow-hidden">
          <InfiniteCanvas
            primitive={primitive}
            render={zoomRenderList}
            board
            singleFrame
            background="inherit"
            hideWidgets={true}
            enableFrameSelection={false}
            enableZoom={true}
            enableDrag={props.enableDrag}
            enableTextSelection={props.enableTextSelection}
            initialZoom={props.initialZoom ?? "full"}
          />
        </div>
      </div>
    );
  }

  const clickHandler = () => {
    const url = primitive?.referenceParameters?.url;
    if (url) {
      window.open(url, "_blank");
    }
  };

  return (
    <div className={`${baseClassName} rounded-lg p-2 border `}>
      <Stage key={primitive?.id} onClick={clickHandler} ref={stageRef} width={width} height={height}>
        <Layer perfectDrawEnabled={false} listening={false}></Layer>
      </Stage>
    </div>
  );
}
